/**
 * Form Instance Model
 * Represents a single instance in multiple form scenarios
 */

import { FormGroup } from '@angular/forms';
import { IFormField } from './form-field.model';

/**
 * Form instance interface for multiple forms
 */
export interface IFormInstance {
  /** Unique identifier for this form instance */
  instanceId: string;
  
  /** Form group for this instance */
  formGroup: FormGroup;
  
  /** Fields configuration (shared across instances) */
  fields: IFormField[];
  
  /** Instance-specific data */
  data?: any;
  
  /** Instance sequence number */
  sequence: number;
  
  /** Whether this instance is collapsed (UI state) */
  collapsed?: boolean;
  
  /** Whether this instance is being deleted */
  isDeleting?: boolean;
  
  /** Custom instance label */
  label?: string;
}

/**
 * Form instance implementation
 */
export class FormInstance implements IFormInstance {
  instanceId: string;
  formGroup: FormGroup;
  fields: IFormField[];
  data?: any;
  sequence: number;
  collapsed?: boolean = false;
  isDeleting?: boolean = false;
  label?: string;
  
  constructor(
    instanceId: string,
    formGroup: FormGroup,
    fields: IFormField[],
    sequence: number,
    data?: any,
    label?: string
  ) {
    this.instanceId = instanceId;
    this.formGroup = formGroup;
    this.fields = fields;
    this.sequence = sequence;
    this.data = data;
    this.label = label || `Form ${sequence}`;
  }
  
  /**
   * Factory method to create a new instance
   */
  static create(
    fields: IFormField[],
    sequence: number,
    data?: any
  ): FormInstance {
    const instanceId = `form_instance_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const formGroup = new FormGroup({});
    
    // Create controls for each field
    fields.forEach(field => {
      if (field.control && field.dbFeildName) {
        formGroup.addControl(field.dbFeildName, field.control);
      }
    });
    
    return new FormInstance(instanceId, formGroup, fields, sequence, data);
  }
  
  /**
   * Toggle collapsed state
   */
  toggleCollapse(): void {
    this.collapsed = !this.collapsed;
  }
  
  /**
   * Mark for deletion
   */
  markForDeletion(): void {
    this.isDeleting = true;
  }
  
  /**
   * Check if instance is valid
   */
  isValid(): boolean {
    return this.formGroup.valid;
  }
  
  /**
   * Get instance data
   */
  getData(): any {
    return this.formGroup.value;
  }
  
  /**
   * Patch instance data
   */
  patchData(data: any): void {
    this.formGroup.patchValue(data);
    this.data = data;
  }
  
  /**
   * Reset instance
   */
  reset(): void {
    this.formGroup.reset();
    this.data = null;
  }
}

/**
 * Multiple forms manager configuration
 */
export interface IMultipleFormsConfig {
  /** Minimum number of forms required */
  minForms?: number;
  
  /** Maximum number of forms allowed */
  maxForms?: number;
  
  /** Allow removing forms */
  allowRemove?: boolean;
  
  /** Allow collapsing forms */
  allowCollapse?: boolean;
  
  /** Show form sequence numbers */
  showSequence?: boolean;
  
  /** Default collapsed state for new forms */
  defaultCollapsed?: boolean;
}
